import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaExternalLinkAlt,
  FaBuilding,
  FaFileInvoice,
} from "react-icons/fa";

import Footer from "../Templete/Footer";
import NavBar from "../Templete/NavBar";
import FormService from "../../Services/FormService";
import LoadingContext from "../../Store/loading-context";
import HttpError from "../../utill/HttpError";

import "./ContactUs.css";
import Header from "../Templete/Header";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  subject: "",
  message: "",
};

// Location coordinates retained from your original map.
const mapUrl =
  "https://www.google.com/maps?q=22.7442159,88.4390018&output=embed";

const directionsUrl =
  "https://www.google.com/maps/dir/?api=1&destination=22.7442159,88.4390018";

const ContactUs = () => {
  const loadingCtx = useContext(LoadingContext);
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    document.title = "Contact Us | Formax IT Solutions Pvt. Ltd.";
  }, []);

  const changeHandler = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;

    const values = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [key, value.trim()]),
    );

    if (Object.values(values).some((value) => !value)) {
      await Swal.fire(
        "Missing information",
        "Please complete all required fields.",
        "error",
      );
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      loadingCtx.startLoading();

      const responseData = await FormService.contactForm(
        `${values.firstName} ${values.lastName}`,
        values.email,
        values.mobile,
        values.subject,
        values.message,
      );

      if (responseData.status === "SUCCESS") {
        setForm({ ...initialForm });

        await Swal.fire(
          "Message sent",
          responseData.message || "Thank you for contacting us.",
          "success",
        );
      } else {
        await Swal.fire(
          "Unable to send",
          responseData.message || "Please try again.",
          "error",
        );
      }
    } catch (error) {
      await Swal.fire(
        "Unable to send",
        HttpError.getError(error) || "Please try again later.",
        "error",
      );
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      loadingCtx.stopLoading();
    }
  };

  return (
    <>
      <Header />

      <main className="fit-contact">
        {/* Page introduction */}
        <section className="fit-contact__hero">
          <div className="fit-contact__container">
            <nav className="fit-contact__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Contact Us</span>
            </nav>

            <span className="fit-contact__eyebrow">LET’S CONNECT</span>

            <h1>
              Good ideas start with
              <br />
              <span>a conversation.</span>
            </h1>

            <p>
              Have a project in mind, a partnership opportunity, or a question
              about our services? We’d like to hear from you.
            </p>
          </div>
        </section>

        <div className="fit-contact__container fit-contact__content">
          {/* Contact cards */}
          <section className="fit-contact__cards" aria-label="Contact options">
            <div className="fit-contact__card">
              <span className="fit-contact__icon">
                <FaPhoneAlt aria-hidden="true" />
              </span>
              <h2>Call us</h2>
              <p>Speak with us about your requirements.</p>
              <a href="tel:+919836114520">
                +91 98361 14520
                <FaArrowRight aria-hidden="true" />
              </a>
            </div>

            <div className="fit-contact__card">
              <span className="fit-contact__icon">
                <FaEnvelope aria-hidden="true" />
              </span>
              <h2>Email us</h2>
              <p>For projects and general enquiries.</p>
              <a href="mailto:info@formaxit.com">
                info@formaxit.com
                <FaArrowRight aria-hidden="true" />
              </a>
            </div>

            <div className="fit-contact__card">
              <span className="fit-contact__icon fit-contact__icon--green">
                <FaBuilding aria-hidden="true" />
              </span>
              <h2>Partner with us</h2>
              <p>Explore business and payment partnerships.</p>
              <a href="mailto:partnership@formaxpay.com">
                partnership@formaxpay.com
                <FaArrowRight aria-hidden="true" />
              </a>
            </div>
          </section>

          {/* Form and information */}
          <section
            className="fit-contact__enquiry"
            aria-labelledby="contact-form-title"
          >
            <div className="fit-contact__form-panel">
              <span className="fit-contact__eyebrow">SEND AN ENQUIRY</span>
              <h2 id="contact-form-title">How can we help?</h2>
              <p className="fit-contact__form-intro">
                Tell us what you’re looking for and our team will get in touch.
                All fields are required.
              </p>

              <form onSubmit={submitHandler} aria-busy={isSubmitting}>
                <fieldset
                  className="fit-contact__fieldset"
                  disabled={isSubmitting}
                >
                  <legend className="fit-contact__sr-only">
                    Your contact details and enquiry
                  </legend>

                  <div className="fit-contact__form-grid">
                    <div className="fit-contact__field">
                      <label htmlFor="contact-first-name">
                        First name <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="contact-first-name"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        placeholder="First name"
                        value={form.firstName}
                        onChange={changeHandler}
                        maxLength={80}
                        required
                      />
                    </div>

                    <div className="fit-contact__field">
                      <label htmlFor="contact-last-name">
                        Last name <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="contact-last-name"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        placeholder="Last name"
                        value={form.lastName}
                        onChange={changeHandler}
                        maxLength={80}
                        required
                      />
                    </div>

                    <div className="fit-contact__field">
                      <label htmlFor="contact-email">
                        Email address <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={changeHandler}
                        maxLength={254}
                        required
                      />
                    </div>

                    <div className="fit-contact__field">
                      <label htmlFor="contact-mobile">
                        Mobile number <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="contact-mobile"
                        name="mobile"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+91 98765 43210"
                        value={form.mobile}
                        onChange={changeHandler}
                        maxLength={25}
                        required
                      />
                    </div>

                    <div className="fit-contact__field fit-contact__field--full">
                      <label htmlFor="contact-subject">
                        Subject <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        placeholder="What would you like to discuss?"
                        value={form.subject}
                        onChange={changeHandler}
                        maxLength={200}
                        required
                      />
                    </div>

                    <div className="fit-contact__field fit-contact__field--full">
                      <label htmlFor="contact-message">
                        Your message <span aria-hidden="true">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        placeholder="Share your requirements or ask us a question..."
                        value={form.message}
                        onChange={changeHandler}
                        rows={6}
                        maxLength={5000}
                        required
                      />
                    </div>
                  </div>

                  <div className="fit-contact__form-bottom">
                    <p>
                      Please avoid including passwords or sensitive financial
                      information.
                    </p>

                    <button type="submit" className="fit-contact__submit">
                      {isSubmitting ? "Sending…" : "Send message"}
                      <FaArrowRight aria-hidden="true" />
                    </button>
                  </div>
                </fieldset>
              </form>
            </div>

            <aside className="fit-contact__office">
              <span className="fit-contact__eyebrow">OUR OFFICE</span>
              <h2>Find us in Barasat.</h2>
              <p className="fit-contact__office-intro">
                Formax IT Solutions Pvt. Ltd.
              </p>

              <div className="fit-contact__address">
                <FaMapMarkerAlt aria-hidden="true" />
                <address>
                  Ground Floor, 16, Mochpole,
                  <br />
                  Near Junior School, Nabapally,
                  <br />
                  Barasat, Kolkata – 700126,
                  <br />
                  West Bengal, India
                </address>
              </div>

              <a
                className="fit-contact__directions"
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get directions
                <FaExternalLinkAlt aria-hidden="true" />
                <span className="fit-contact__sr-only">
                  (opens in a new tab)
                </span>
              </a>

              <div className="fit-contact__registration">
                <div>
                  <FaBuilding aria-hidden="true" />
                  <span>
                    <small>CIN</small>
                    <strong>U72900WB2018PTC228240</strong>
                  </span>
                </div>

                <div>
                  <FaFileInvoice aria-hidden="true" />
                  <span>
                    <small>GSTIN</small>
                    <strong>19AADCF5125F1ZT</strong>
                  </span>
                </div>
              </div>
            </aside>
          </section>

          {/* Location map */}
          <section
            className="fit-contact__map-section"
            aria-labelledby="contact-map-title"
          >
            <div className="fit-contact__map-heading">
              <div>
                <span className="fit-contact__eyebrow">OUR LOCATION</span>
                <h2 id="contact-map-title">Plan your visit</h2>
              </div>
              <p>Barasat, Kolkata · West Bengal</p>
            </div>

            <div className="fit-contact__map">
              <iframe
                title="Formax IT Solutions office location"
                src={mapUrl}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ContactUs;
